import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type {
  ConcentrationResponse,
  DrawdownResponse,
  ExposureProvenance,
  Measure,
  UnitSeriesResponse,
  UnitsOverview,
  YearEndListing,
  YearEndPin,
} from '@/api/analytics'
import type { DatedProvenance, Provenance } from '@/api/networth'
import type { CashflowKind } from '@/domain/cashflow'
import type { ConcentrationView } from '@/domain/risk'
import type { EffectiveValuation, HoldingsResponse } from '~/api/client'
import { apiFetch, withQuery } from '~/api/client'
import type { NetWorthSnapshot } from '~/charts/data'
import { adoptBaseCurrency } from '~/format/locale'
import type { HealthReport } from '~/state/health'
import { deriveHealth } from '~/state/health'
import type { PrivateHoldingsSettings } from '~/state/private-holdings'
import {
  PRIVATE_HOLDINGS_SETTING_KEY,
  concentrationParams,
  concentrationView,
  includesPrivateHoldings,
  mergePrivateHoldingsSettings,
  parsePrivateHoldingsSettings,
  valuationParams,
} from '~/state/private-holdings'

export type {
  Measure,
  ConcentrationView,
  DatedProvenance,
  DrawdownResponse,
  ExposureProvenance,
  Provenance,
  YearEndListing,
  YearEndPin,
}

const SETTINGS_KEY = ['settings'] as const

/**
 * Whether the household holds anything through an entity other than itself —
 * the fact the measures that speak of a company are offered on.
 *
 * Asked under the household's own private-holdings switch, because the pages
 * that call this compute their figures under it too. Look-through and Spendable
 * share that switch, so a company holding nothing but private holdings, with
 * private holdings switched out, contributes to neither — and a Spendable
 * offered there would be Look-through again, which is the defect this gate
 * exists to remove rather than a new place to repeat it. `lookThrough` names
 * the lens rather than the figure: the chosen measure cannot decide what is
 * offered without deciding itself.
 *
 * Undefined until it arrives: a measure offered and then taken away reads as a
 * fault, so a caller waits rather than guesses.
 */
export function useHoldsThroughCompany(
  settings: PrivateHoldingsSettings,
): boolean | undefined {
  const params = valuationParams(settings, 'lookThrough')
  const snapshot = useQuery({
    queryKey: ['networth', 'current', params],
    queryFn: () => apiFetch<NetWorthSnapshot>(withQuery('/networth/current', params)),
    retry: false,
  })
  return snapshot.data?.holdsThroughCompany
}

/**
 * Both facts the figure pickers are offered on, read from the same snapshot as
 * `useHoldsThroughCompany` and under the same private-holdings switch — a
 * private holding switched out is not held for Liquid to subtract either.
 *
 * Undefined until it arrives, for the same reason.
 */
export function useHouseholdShape(
  settings: PrivateHoldingsSettings,
): { company: boolean; unsellable: boolean } | undefined {
  const params = valuationParams(settings, 'lookThrough')
  const snapshot = useQuery({
    queryKey: ['networth', 'current', params],
    queryFn: () => apiFetch<NetWorthSnapshot>(withQuery('/networth/current', params)),
    retry: false,
  })
  const data = snapshot.data
  return data === undefined
    ? undefined
    : { company: data.holdsThroughCompany, unsellable: data.holdsUnsellable }
}

interface SettingsResponse {
  settings: Record<string, unknown>
  environment?: 'production' | 'development'
  /** True once the seed has touched this database, and never cleared after. */
  demoData?: boolean
}

/**
 * The household's own currency, which every page's figures are shaped by. It is
 * read here because this is the one request every page already makes before it
 * paints a number, and a figure formatted before the answer arrived would have
 * to repaint itself in front of the reader.
 */
const BASE_CURRENCY_SETTING_KEY = 'baseCurrency'

export function useSettings(): UseQueryResult<SettingsResponse> {
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: async () => {
      const response = await apiFetch<SettingsResponse>('/settings')
      const stored = response.settings[BASE_CURRENCY_SETTING_KEY]
      adoptBaseCurrency(typeof stored === 'string' ? stored : undefined)
      return response
    },
  })
}

export interface PrivateHoldingsStore {
  settings: PrivateHoldingsSettings
  isPending: boolean
  isSaving: boolean
  error: Error | null
  save: (next: PrivateHoldingsSettings) => void
}

export function usePrivateHoldings(): PrivateHoldingsStore {
  const client = useQueryClient()
  const settingsQuery = useSettings()
  const stored = settingsQuery.data?.settings[PRIVATE_HOLDINGS_SETTING_KEY]

  const mutation: UseMutationResult<unknown, Error, PrivateHoldingsSettings> = useMutation({
    mutationFn: (next: PrivateHoldingsSettings) =>
      apiFetch(`/settings/keys/${PRIVATE_HOLDINGS_SETTING_KEY}`, {
        method: 'PUT',
        body: JSON.stringify({ value: mergePrivateHoldingsSettings(stored, next) }),
      }),
    // A global switch changes every figure on every page, not just this one.
    onSuccess: () => client.invalidateQueries(),
  })

  // The switch moves the moment it is clicked; the round trip only confirms it.
  const settings =
    mutation.isPending && mutation.variables
      ? mutation.variables
      : parsePrivateHoldingsSettings(stored)

  return {
    settings,
    isPending: settingsQuery.isPending,
    isSaving: mutation.isPending,
    error: settingsQuery.error ?? mutation.error,
    save: mutation.mutate,
  }
}

export function useHealth(date?: string): UseQueryResult<HealthReport> {
  return useQuery({
    queryKey: ['health', date ?? 'latest'],
    queryFn: async () => deriveHealth(await apiFetch<HoldingsResponse>(withQuery('/holdings', { date }))),
  })
}

export function useEffectiveValuations(
  date?: string,
): UseQueryResult<{ date: string; valuations: EffectiveValuation[] }> {
  return useQuery({
    queryKey: ['valuations', 'effective', date ?? 'latest'],
    queryFn: () => apiFetch(withQuery('/manual/valuations/effective', { date })),
  })
}

/**
 * `enabled` defaults open for the page this overview draws, but the navigation
 * asks the same question before it knows whether anybody is signed in — it
 * passes `false` until a household is, so a visitor sees no units request
 * before the session does.
 */
export function useUnitsOverview(
  settings: PrivateHoldingsSettings,
  measure: Measure,
  enabled = true,
): UseQueryResult<UnitsOverview> {
  const params = { ...valuationParams(settings, measure), measure }
  return useQuery({
    queryKey: ['units', 'overview', params],
    queryFn: () => apiFetch<UnitsOverview>(withQuery('/analytics/units', params)),
    enabled,
  })
}

export function useUnitSeries(
  settings: PrivateHoldingsSettings,
  measure: Measure,
  days: number,
): UseQueryResult<UnitSeriesResponse> {
  const params = {
    ...valuationParams(settings, measure),
    measure,
    days: String(days),
  }
  return useQuery({
    queryKey: ['units', 'series', params],
    queryFn: () => apiFetch<UnitSeriesResponse>(withQuery('/analytics/units/series', params)),
  })
}

export interface MonthPerformanceRow {
  month: string
  openingDate: string
  closingDate: string
  openingMinor: number
  closingMinor: number
  changeMinor: number
  contributionsMinor: number
  marketReturnMinor: number
  inMinor: number
  outMinor: number
  internalTransferMinor: number
  assetSwapMinor: number
  byKind: Partial<Record<CashflowKind, number>>
  returnBp: number | null
  partial: boolean
}

export interface PerformanceResponse {
  baseCurrency: string
  figure: Measure
  from: string
  to: string
  openingMinor: number
  closingMinor: number
  changeMinor: number
  contributionsMinor: number
  marketReturnMinor: number
  returnBp: number | null
  missingFx: string[]
  /** One entry per day the figures were read from, so the decomposition can say where it came from. */
  provenance: DatedProvenance[]
  months: MonthPerformanceRow[]
}

export interface RunwayResponse extends Provenance {
  baseCurrency: string
  date: string
  liquidMinor: number
  averageSpendMinor: number
  monthsUsed: string[]
  runwayMonths: string | null
  runwayMonthsBp: number | null
  complete: boolean
  missingFx: string[]
  spend: { month: string; spendMinor: number }[]
}

export interface TransferRow {
  month: string
  fromAccountId: number
  toAccountId: number
  outDate: string
  inDate: string
  gapDays: number
  amountMinor: number
  residualMinor: number
}

export interface TransfersResponse {
  baseCurrency: string
  from?: string
  to: string
  missingFx: string[]
  transfers: TransferRow[]
  unmatched: { id: number; date: string; accountId: number; kind: CashflowKind; amountMinor: number }[]
}

export function usePerformance(
  settings: PrivateHoldingsSettings,
  figure: Measure,
  months: number,
): UseQueryResult<PerformanceResponse> {
  const params = { ...valuationParams(settings, figure), figure, months: String(months) }
  return useQuery({
    queryKey: ['performance', 'monthly', params],
    queryFn: () => apiFetch<PerformanceResponse>(withQuery('/performance/monthly', params)),
    retry: false,
  })
}

/**
 * The transfers the monthly figure netted out are only evidence about that
 * figure, so they are asked for over the same range and the same account set.
 */
export function useTransfers(
  settings: PrivateHoldingsSettings,
  figure: Measure,
  from: string | undefined,
): UseQueryResult<TransfersResponse> {
  const params = { ...valuationParams(settings, figure), ...(from ? { from } : {}) }
  return useQuery({
    queryKey: ['performance', 'transfers', params],
    queryFn: () => apiFetch<TransfersResponse>(withQuery('/performance/transfers', params)),
    enabled: from !== undefined,
    retry: false,
  })
}

export function useRunway(
  settings: PrivateHoldingsSettings,
  trailingMonths: number,
): UseQueryResult<RunwayResponse> {
  const params = { ...valuationParams(settings, 'runway'), months: String(trailingMonths) }
  return useQuery({
    queryKey: ['performance', 'runway', params],
    queryFn: () => apiFetch<RunwayResponse>(withQuery('/performance/runway', params)),
    retry: false,
  })
}

export interface ConcentrationSelection extends ConcentrationResponse {
  /** The view the private-holdings switch asks for, already chosen. */
  view: ConcentrationView
  includesPrivate: boolean
  /** Keyed by exposure, because the chosen view drops rows the response still carries. */
  origins: Map<string, ExposureProvenance>
}

export function useConcentration(
  settings: PrivateHoldingsSettings,
  date?: string,
): UseQueryResult<ConcentrationSelection> {
  const params = { ...concentrationParams(settings), ...(date ? { date } : {}) }
  return useQuery({
    queryKey: ['analytics', 'concentration', params],
    queryFn: () => apiFetch<ConcentrationResponse>(withQuery('/analytics/concentration', params)),
    select: (response) => ({
      ...response,
      view: concentrationView(response, settings),
      includesPrivate: includesPrivateHoldings(settings, 'concentration'),
      origins: new Map(response.provenance.map((origin) => [origin.key, origin])),
    }),
    retry: false,
  })
}

export function useDrawdown(
  settings: PrivateHoldingsSettings,
  measure: Measure,
  days: number,
): UseQueryResult<DrawdownResponse> {
  const params = { ...valuationParams(settings, measure), measure, days: String(days) }
  return useQuery({
    queryKey: ['analytics', 'drawdown', params],
    queryFn: () => apiFetch<DrawdownResponse>(withQuery('/analytics/drawdown', params)),
    retry: false,
  })
}

export function useYearEnds(): UseQueryResult<YearEndListing> {
  return useQuery({
    queryKey: ['analytics', 'year-ends'],
    queryFn: () => apiFetch<YearEndListing>('/analytics/year-ends'),
    retry: false,
  })
}

export function usePinYearEnd(): UseMutationResult<{ pin: YearEndPin; created: boolean }, Error, number> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (year: number) =>
      apiFetch<{ pin: YearEndPin; created: boolean }>(`/analytics/year-ends/${year}`, {
        method: 'POST',
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['analytics', 'year-ends'] }),
  })
}
